import React, { useState, useEffect } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { checklistAPI, referralAPI } from '../services/api';
import { hasPermission } from '../utils/permissions';
import ContentHeader from '../components/ContentHeader';
import { formatDisplayDate } from '../utils/dateFormat';
import './ReferralChecklists.css';
function formatDate(d) {
  return formatDisplayDate(d);
}

function ReferralChecklists({ referralIdOverride, clientIdOverride }) {
  const { id: referralIdFromParams } = useParams();
  const location = useLocation();
  const { tenantPath } = useTenantFromUrl();
  const referralId = referralIdOverride ?? referralIdFromParams;
  const clientId = clientIdOverride ?? location.state?.clientId ?? null;

  const [referral, setReferral] = useState(null);
  const [groups, setGroups] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addTypeId, setAddTypeId] = useState('');
  const [moreInfoFor, setMoreInfoFor] = useState(null);
  const [includeClosed, setIncludeClosed] = useState(false);

  const loadReferral = async () => {
    if (!referralId) return;
    try {
      const res = await referralAPI.getById(referralId);
      setReferral(res.data?.data ?? null);
    } catch {
      setReferral(null);
    }
  };

  const loadList = async () => {
    if (!referralId) return;
    try {
      setError(null);
      const res = await checklistAPI.listByReferralId(referralId);
      setGroups(res.data?.data ?? []);
    } catch (err) {
      const msg = err.response?.status === 404
        ? 'Checklist feature is not enabled for this tenant.'
        : (err.response?.data?.error || err.message);
      setError(msg);
      setGroups([]);
    }
  };

  const loadTypes = async () => {
    try {
      const res = await checklistAPI.getTypes();
      setTypes(res.data?.data ?? []);
    } catch {
      setTypes([]);
    }
  };

  useEffect(() => {
    if (!referralId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    Promise.all([loadReferral(), loadList(), loadTypes()]).then(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [referralId]);

  // When navigated from Dashboard checklists with a specific checklistId, open that checklist
  const checklistIdFromState = location.state?.checklistId;
  useEffect(() => {
    if (!checklistIdFromState || !groups.length) return;
    const exists = groups.some((g) => String(g.id) === String(checklistIdFromState));
    if (exists) setSelectedId(String(checklistIdFromState));
  }, [groups, checklistIdFromState]);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    checklistAPI.getById(selectedId)
      .then((res) => {
        if (!cancelled) {
          setDetail(res.data?.data ?? null);
        }
      })
      .catch(() => {
        if (!cancelled) setDetail(null);
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => { cancelled = true; };
  }, [selectedId]);

  const handleAddChecklist = async () => {
    if (!addTypeId || !referralId) return;
    setSaving(true);
    try {
      await checklistAPI.createForReferral(referralId, { checklist_type_id: addTypeId });
      setAddOpen(false);
      setAddTypeId('');
      await loadList();
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Failed to add checklist');
    } finally {
      setSaving(false);
    }
  };

  const handleCloseChange = async (checklistId, closed) => {
    setSaving(true);
    try {
      await checklistAPI.update(checklistId, { closed });
      if (selectedId === checklistId) {
        const res = await checklistAPI.getById(checklistId);
        setDetail(res.data?.data ?? null);
      }
      await loadList();
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleItemCompleted = async (checklistId, item, completed) => {
    setSaving(true);
    try {
      const nextCompleted = completed || null;
      let createWorkTask = false;
      const becameYes = nextCompleted === 'yes' && item?.completed !== 'yes';
      const hasWorkTaskMapping = !!item?.work_type_id && !!item?.task_type_id;
      if (becameYes && hasWorkTaskMapping) {
        createWorkTask = window.confirm(
          `Create work task now?\n\nWork type: ${item.work_type_name || 'Mapped'}\nTask type: ${item.task_type_name || 'Mapped'}`
        );
      }
      await checklistAPI.updateItem(checklistId, item.id, {
        completed: nextCompleted,
        create_work_task: createWorkTask,
      });
      await loadList();
      if (selectedId === checklistId) {
        const res = await checklistAPI.getById(selectedId);
        setDetail(res.data?.data ?? null);
      }
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Failed to update item');
    } finally {
      setSaving(false);
    }
  };

  const clientName = (referral?.client_last_name != null || referral?.client_first_name != null)
    ? [referral.client_last_name, referral.client_first_name].filter(Boolean).join(', ')
    : null;
  const clientDob = referral?.client_date_of_birth ? formatDate(referral.client_date_of_birth) : null;

  if (loading) {
    return (
      <div className="referral-checklists-page">
        <ContentHeader title="Checklists" />
        <p className="loading-msg">Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="referral-checklists-page">
        <ContentHeader title="Checklists" />
        <p className="error-msg">{error}</p>
      </div>
    );
  }

  const canClose = detail?.items?.every((i) => !i.required || i.completed === 'yes') ?? false;
  const requiredIncomplete = detail?.items?.filter((i) => i.required && i.completed !== 'yes').length ?? 0;

  return (
    <div className="referral-checklists-page">
      <ContentHeader
        title="Checklists"
        actions={
          hasPermission('checklists.create') ? (
            <button
              type="button"
              className="btn-add-checklist"
              onClick={() => setAddOpen(true)}
            >
              + Add checklist
            </button>
          ) : null
        }
      />

      {(!clientId && (clientName || clientDob) || referral?.open_date != null) && (
        <section className="referral-checklists-context referral-context">
          <p className="referral-context-line referral-context-primary">
            {!clientId && clientName && (
              <>
                <strong>Client:</strong> {clientName}
                {clientDob && <> · <strong>DOB:</strong> {clientDob}</>}
              </>
            )}
            {referral?.open_date != null && (
              <>
                {!clientId && clientName && ' · '}
                <strong>Open:</strong> {formatDate(referral.open_date)}
                {referral?.close_date && <> · <strong>Closed:</strong> {formatDate(referral.close_date)}</>}
              </>
            )}
          </p>
        </section>
      )}

      {addOpen && (
        <div className="referral-checklists-add-modal" onClick={() => !saving && setAddOpen(false)}>
          <div className="referral-checklists-add-content" onClick={(e) => e.stopPropagation()}>
            <h3>Add checklist</h3>
            <select
              value={addTypeId}
              onChange={(e) => setAddTypeId(e.target.value)}
              className="add-type-select"
            >
              <option value="">Select type...</option>
              {types
                .filter((t) => t.is_active !== false)
                .map((t) => (
                  <option key={t.id} value={t.id}>{t.type_name}</option>
                ))}
            </select>
            {types.filter((t) => t.is_active !== false).length === 0 && (
              <p className="add-no-types">All checklist types already added for this referral.</p>
            )}
            <div className="add-actions">
              <button type="button" className="btn-cancel" onClick={() => setAddOpen(false)} disabled={saving}>
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={handleAddChecklist}
                disabled={saving || !addTypeId}
              >
                {saving ? 'Adding...' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}

      <section className="referral-checklists-list">
        <div className="referral-checklists-list-header">
          <h2>{includeClosed ? 'Checklist groups (including closed)' : 'Active checklist groups'}</h2>
          <label className="include-closed-label">
            <input
              type="checkbox"
              checked={includeClosed}
              onChange={(e) => setIncludeClosed(e.target.checked)}
            />
            <span>Include closed</span>
          </label>
        </div>
        {(() => {
          const displayedGroups = includeClosed ? groups : groups.filter((g) => !g.closed);
          if (displayedGroups.length === 0) {
            return (
              <p className="no-checklists">
                {includeClosed
                  ? 'No checklist groups for this referral.'
                  : 'No active checklists. Add one to get started, or check Include closed to see closed groups.'}
              </p>
            );
          }
          return (
          <div className="checklist-groups">
            {displayedGroups.map((g) => (
              <div
                key={g.id}
                className={`checklist-group-card ${selectedId === g.id ? 'selected' : ''}`}
              >
                <div className="group-card-main">
                  <div
                    className="group-card-clickable"
                    onClick={() => setSelectedId(selectedId === g.id ? null : g.id)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedId(selectedId === g.id ? null : g.id); } }}
                    role="button"
                    tabIndex={0}
                  >
                    {clientName && <span className="group-client">{clientName}</span>}
                    <span className="group-type">{g.type_name}</span>
                    <span className="group-meta">
                      Created {formatDate(g.created_at)}
                      {g.closed && <span className="badge-closed">Closed</span>}
                    </span>
                  </div>
                  {hasPermission('checklists.update') && (
                    <div className="group-actions" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className={g.closed ? 'btn btn-secondary' : 'btn btn-primary'}
                        disabled={saving || (!g.closed && selectedId === g.id && detail && !detail.closed && !canClose)}
                        onClick={() => handleCloseChange(g.id, !g.closed)}
                      >
                        {g.closed ? 'Reopen' : 'Close'}
                      </button>
                    </div>
                  )}
                </div>
                {selectedId === g.id && (
                  <div className="group-detail">
                    {detailLoading ? (
                      <p className="detail-loading">Loading items...</p>
                    ) : detail && detail.id === g.id ? (
                      <>
                        {detail.type_notes && (
                          <p className="type-notes">{detail.type_notes}</p>
                        )}
                        <div className="close-validation">
                          {!canClose && detail.closed === false && requiredIncomplete > 0 && (
                            <p className="close-warning">
                              Complete all required items (Yes) before closing. ({requiredIncomplete} required incomplete)
                            </p>
                          )}
                        </div>
                        <div className="items-table-wrap">
                          <table className="items-table">
                          <thead>
                            <tr>
                              <th>Item</th>
                              <th className="col-required">Required</th>
                              <th className="col-completed">Completed</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(detail.items || []).map((item) => {
                              const isIncompleteRequired = !detail.closed && item.required && item.completed !== 'yes';
                              const showHighlight = !canClose && requiredIncomplete > 0;
                              return (
                              <tr
                                key={item.id}
                                className={showHighlight && isIncompleteRequired ? 'row-required-incomplete' : ''}
                              >
                                <td className="item-desc">
                                  {item.item_description}
                                  {item.template_is_active === false && (
                                    <span className="badge-template-inactive" title="This line is retired from the template; it still appears on this checklist.">
                                      Retired line
                                    </span>
                                  )}
                                  {item.more_info && (
                                    <button
                                      type="button"
                                      className="btn-more-info"
                                      onClick={() => setMoreInfoFor(moreInfoFor === item.id ? null : item.id)}
                                    >
                                      {moreInfoFor === item.id ? 'Hide details' : 'More info'}
                                    </button>
                                  )}
                                  {moreInfoFor === item.id && item.more_info && (
                                    <div
                                      className="item-more-info"
                                      dangerouslySetInnerHTML={{ __html: item.more_info }}
                                    />
                                  )}
                                </td>
                                <td className="col-required">
                                  {item.required ? <span className="badge-required">Required</span> : '—'}
                                </td>
                                <td className="col-completed">
                                  {hasPermission('checklists.update') ? (
                                    <select
                                      value={item.required && item.completed === 'na' ? '' : (item.completed ?? '')}
                                      onChange={(e) => handleItemCompleted(detail.id, item, e.target.value || null)}
                                      disabled={saving}
                                      className="completed-select"
                                    >
                                      <option value="">—</option>
                                      <option value="yes">Yes</option>
                                      {!item.required && <option value="na">NA</option>}
                                    </select>
                                  ) : (
                                    <span>{item.completed === 'yes' ? 'Yes' : item.completed === 'na' ? 'NA' : '—'}</span>
                                  )}
                                </td>
                              </tr>
                            ); })}
                          </tbody>
                        </table>
                        </div>
                      </>
                    ) : null}
                  </div>
                )}
              </div>
            ))}
          </div>
          );
        })()}
      </section>
    </div>
  );
}

export default ReferralChecklists;
